import { useRef, useState, type ChangeEvent } from 'react';
import { Box, Button, Alert } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { FieldValue } from '../../../../types/QueryBuilder';
import { useLanguage } from '../../../../context/LanguageContext';

interface StoreListImportProps {
    values: FieldValue[];
    selectedValue?: string | string[] | number | null;
    onChange: (value: string[]) => void;
}

const normalizeIdentifier = (value: string): string => (
    value
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
);

const firstCellOf = (line: string): string => (line.split(/[;,\t|]/)[0] ?? line).trim();

// Store identifiers are formatted as "<code> <name>" (e.g. "000041 ROCHIIETTA") — match on the code only.
const codeOf = (value: string): string => value.trim().split(/\s+/)[0] ?? '';

// Store codes are numeric — a non-numeric first line is treated as a header and skipped.
const looksLikeCode = (value: string): boolean => /^\d+$/.test(value);

function StoreListImport({ values, selectedValue, onChange }: StoreListImportProps) {
    const { t } = useLanguage();
    const inputRef = useRef<HTMLInputElement>(null);
    const [unrecognized, setUnrecognized] = useState<string[]>([]);

    const handleClick = () => inputRef.current?.click();

    const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (inputRef.current) inputRef.current.value = '';
        if (!file) return;

        const text = await file.text();
        const lines = text
            .split(/\r?\n/)
            .map(firstCellOf)
            .filter(Boolean);

        const nameByCode = new Map<string, string>();
        values.forEach(option => {
            nameByCode.set(normalizeIdentifier(codeOf(option.name)), option.name);
            if (option.label) nameByCode.set(normalizeIdentifier(codeOf(option.label)), option.name);
        });

        const matched = new Set<string>();
        const notRecognized: string[] = [];

        lines.forEach((line, index) => {
            const matchedName = nameByCode.get(normalizeIdentifier(codeOf(line)));
            if (matchedName) {
                matched.add(matchedName);
                return;
            }
            if (index === 0 && !looksLikeCode(codeOf(line))) {
                return;
            }
            notRecognized.push(line);
        });

        setUnrecognized(notRecognized);
        onChange(Array.from(matched));
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<UploadFileIcon fontSize="small" />}
                    onClick={handleClick}
                >
                    {t('pages.clients.queryBuilder.simple.importStores.button')}
                </Button>
                <input
                    ref={inputRef}
                    type="file"
                    accept=".csv,.txt"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                />
            </Box>
            {unrecognized.length > 0 && (
                <Alert severity="warning" sx={{ py: 0.5 }} onClose={() => setUnrecognized([])}>
                    {t('pages.clients.queryBuilder.simple.importStores.unrecognized').replace('{count}', String(unrecognized.length))}
                    {': '}
                    {unrecognized.join(', ')}
                </Alert>
            )}
        </Box>
    );
}

export default StoreListImport;
